import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Award, Flame, Save, Target as TargetIcon, TrendingUp, Trophy } from "lucide-react";
import { journeyApi, type CompoundRow } from "../lib/api";
import { formatCurrency, formatSignedCurrency } from "../lib/format";
import { useI18n } from "../lib/i18n";

type TFunc = (key: string, vars?: Record<string, string | number>) => string;

function formatEta(t: TFunc, days: number): string {
  if (days <= 0) return t("journey.etaHitIt");
  if (days <= 60) return t(days === 1 ? "journey.etaDay" : "journey.etaDays", { days });
  const months = Math.round(days / 30);
  if (months <= 24) return t(months === 1 ? "journey.etaMonth" : "journey.etaMonths", { months });
  const years = Math.round(days / 365);
  return t(years === 1 ? "journey.etaYear" : "journey.etaYears", { years });
}

function MetricCard({
  Icon,
  label,
  value,
  sub,
  accent,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="glass p-5">
      <div className="flex items-center gap-2 mb-2 text-parchment-muted">
        <Icon className="w-4 h-4" />
        <p className="label">{label}</p>
      </div>
      <p className={`text-2xl font-bold tabular ${accent ?? "text-parchment"}`}>{value}</p>
      {sub && <p className="text-xs text-parchment-muted mt-1">{sub}</p>}
    </div>
  );
}

const TIER_STYLES: Record<string, string> = {
  Bronze: "tier-bronze",
  Silver: "tier-silver",
  Gold: "tier-gold",
  Platinum: "tier-platinum",
  Diamond: "tier-diamond",
};

export default function JourneyPage() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["journey"], queryFn: () => journeyApi.show() });

  const [editing, setEditing] = useState(false);
  const [goal, setGoal] = useState("100000");
  const [stepPct, setStepPct] = useState("10");
  const [startBalance, setStartBalance] = useState("1000");
  const [error, setError] = useState<string | null>(null);
  const [showAllLevels, setShowAllLevels] = useState(false);

  const saveM = useMutation({
    mutationFn: () =>
      journeyApi.saveTarget({
        goal_amount: parseFloat(goal),
        level_step_pct: parseFloat(stepPct),
        start_balance: parseFloat(startBalance),
      }),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ["journey"] });
      qc.invalidateQueries({ queryKey: ["wallet"] });
    },
    onError: (e) => setError((e as Error).message),
  });

  const progress = q.data?.progress;
  const target = q.data?.target;
  const table = q.data?.compound_table ?? [];
  const DEFAULT_LEVELS = 30;
  const visibleTable = showAllLevels ? table : table.slice(0, DEFAULT_LEVELS + 1);

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const g = parseFloat(goal);
    const s = parseFloat(stepPct);
    if (!Number.isFinite(g) || g <= 0) {
      setError(t("journey.errGoal"));
      return;
    }
    if (!Number.isFinite(s) || s <= 0 || s > 100) {
      setError(t("journey.errStep"));
      return;
    }
    const a = parseFloat(startBalance);
    if (!Number.isFinite(a) || a <= 0) {
      setError(t("journey.errAnchor"));
      return;
    }
    saveM.mutate();
  }

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("nav.journey")}</p>
          <h1 className="text-3xl font-bold text-parchment mt-1 flex items-center gap-2">
            <TargetIcon className="w-6 h-6 text-accent" />
            {t("journey.title")}
          </h1>
        </div>
        <button
          type="button"
          onClick={() => {
            if (target) {
              setGoal(String(target.goal_amount));
              setStepPct(String(target.level_step_pct));
              setStartBalance(String(target.start_balance));
            }
            setEditing((v) => !v);
          }}
          className="btn-ghost"
        >
          <Save className="w-4 h-4" />
          {target ? t("journey.editTarget") : t("journey.setTarget")}
        </button>
      </header>

      {/* Hero card */}
      <section className="glass-gold p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-glow pointer-events-none" />
        {q.isLoading && <p className="text-parchment-muted">{t("common.loading")}</p>}
        {progress && (
          <div className="relative grid gap-6 lg:grid-cols-[1.2fr_1.8fr] items-center">
            <div>
              <p className="text-parchment-muted text-xs uppercase tracking-widest">{t("journey.currentTier")}</p>
              <div className="mt-1 flex items-center gap-3">
                <span
                  className={
                    "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-bold uppercase tracking-widest " +
                    (TIER_STYLES[progress.tier] ?? "text-parchment border-glass-mid bg-surface-2")
                  }
                >
                  <Trophy className="w-4 h-4" />
                  {progress.tier}
                </span>
                <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gold-linear">
                  L{progress.level}
                </span>
              </div>
              <p className="mt-3 text-sm text-parchment-muted">
                {t("journey.balanceLabel")}{" "}
                <span className="tabular text-parchment font-semibold">
                  {formatCurrency(progress.balance)}
                </span>
              </p>
              <p className="text-sm text-parchment-muted">
                {t("journey.anchorLabel")}{" "}
                <span className="tabular">{formatCurrency(progress.start_balance)}</span> ·{" "}
                {t("journey.step")} {progress.step_pct.toFixed(1)}%
              </p>
            </div>

            <div>
              <div className="flex justify-between text-xs text-parchment-muted mb-1 tabular">
                <span>{formatCurrency(progress.current_level_balance)}</span>
                <span>{formatCurrency(progress.next_level_balance)}</span>
              </div>
              <div className="h-3 rounded-full bg-surface-2 border border-glass overflow-hidden">
                <div
                  className="h-full bg-gold-linear transition-all duration-500"
                  style={{ width: `${progress.level_progress_pct}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-parchment-muted flex flex-wrap gap-x-2">
                <span>
                  {t("journey.toLevel", {
                    pct: progress.level_progress_pct.toFixed(0),
                    level: progress.level + 1,
                  })}
                </span>
                {/* The percentage alone does not say how much more to make. */}
                <span className="tabular text-accent">
                  {t("dash.toGo", { amount: formatCurrency(progress.to_next_level) })}
                </span>
              </p>

              {progress.goal_amount != null && (
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-parchment-muted mb-1 tabular">
                    <span>{t("journey.goalProgress")}</span>
                    <span>
                      {t("journey.goalOf", {
                        pct: progress.goal_progress_pct?.toFixed(1) ?? "",
                        amount: formatCurrency(progress.goal_amount),
                      })}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-surface-2 border border-glass overflow-hidden">
                    <div
                      className="h-full bg-emerald-400/70"
                      style={{ width: `${progress.goal_progress_pct ?? 0}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Streak / level-up / ETA */}
      {progress && (
        <section className="grid gap-4 sm:grid-cols-3">
          <MetricCard
            Icon={Flame}
            label={t("journey.streak")}
            value={progress.streak_days === 0 ? t("common.none") : t("journey.streakDays", { days: progress.streak_days })}
            sub={progress.streak_days > 0 ? t("journey.streakSub") : t("journey.streakSubEmpty")}
            accent={progress.streak_days > 0 ? "text-accent" : "text-parchment-muted"}
          />
          <MetricCard
            Icon={Trophy}
            label={t("journey.lastLevelUp")}
            value={
              progress.days_since_level_up == null
                ? t("journey.noneYet")
                : progress.days_since_level_up === 0
                  ? t("common.today")
                  : t("journey.daysAgo", { days: progress.days_since_level_up })
            }
            sub={
              progress.days_since_level_up == null
                ? t("journey.levelUpSub1")
                : progress.days_since_level_up === 0
                  ? t("journey.levelUpSubFresh")
                  : t("journey.levelUpSubDays")
            }
            accent={
              progress.days_since_level_up != null && progress.days_since_level_up <= 7
                ? "text-pnl-pos"
                : "text-parchment"
            }
          />
          <MetricCard
            Icon={TrendingUp}
            label={t("journey.etaToGoal")}
            value={
              progress.goal_amount == null
                ? t("journey.setAGoal")
                : progress.eta_days_to_goal == null
                  ? t("journey.trendingFlat")
                  : formatEta(t, progress.eta_days_to_goal)
            }
            sub={
              progress.avg_daily_pl_30d == null
                ? undefined
                : t("journey.avgDaily", { amount: formatSignedCurrency(progress.avg_daily_pl_30d) })
            }
            accent={
              progress.eta_days_to_goal != null && progress.eta_days_to_goal < 365
                ? "text-pnl-pos"
                : "text-parchment"
            }
          />
        </section>
      )}

      {editing && (
        <section className="glass p-5">
          <h2 className="text-lg font-semibold text-parchment mb-4">{t("journey.targetHeading")}</h2>
          <form onSubmit={submit} className="grid gap-4 max-w-lg">
            <label className="grid gap-2">
              <span className="label">{t("journey.anchorUsd")}</span>
              <input
                type="number"
                min="0.01"
                step="any"
                required
                value={startBalance}
                onChange={(e) => setStartBalance(e.target.value)}
                className="input"
                placeholder={t("journey.anchorPlaceholder")}
              />
              <span className="text-xs text-parchment-muted">
                {t("journey.anchorHelp")}
              </span>
            </label>
            <label className="grid gap-2">
              <span className="label">{t("journey.goalUsd")}</span>
              <input
                type="number"
                min="1"
                step="1"
                required
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="input"
                placeholder={t("journey.goalPlaceholder")}
              />
            </label>
            <label className="grid gap-2">
              <span className="label">{t("journey.levelStep")}</span>
              <input
                type="number"
                min="0.1"
                step="0.1"
                required
                value={stepPct}
                onChange={(e) => setStepPct(e.target.value)}
                className="input"
              />
              <span className="text-xs text-parchment-muted">
                {t("journey.stepHelp")}
              </span>
            </label>

            {error && (
              <p className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-sm text-pnl-neg">
                {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(false)} className="btn-ghost">
                {t("common.cancel")}
              </button>
              <button type="submit" disabled={saveM.isPending} className="btn-gold">
                {saveM.isPending ? t("common.saving") : t("journey.saveTarget")}
              </button>
            </div>
          </form>
        </section>
      )}

      {/* Compound table */}
      {table.length > 0 && progress && (
        <section className="glass p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-parchment mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-accent" />
            {t("journey.compoundTable")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular">
              <thead className="text-parchment-muted text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-start py-2 pe-4">{t("journey.colLevel")}</th>
                  <th className="text-start py-2 pe-4">{t("journey.colTier")}</th>
                  <th className="text-end py-2 pe-4">{t("journey.colBalance")}</th>
                  <th className="text-end py-2">{t("journey.colGain")}</th>
                </tr>
              </thead>
              <tbody>
                {visibleTable.map((r: CompoundRow) => {
                  const isCurrent = r.level === progress.level;
                  const rowCls =
                    r.balance > progress.balance
                      ? "text-parchment-muted"
                      : isCurrent
                        ? "text-accent font-bold bg-gold-500/15"
                        : "text-parchment";
                  return (
                    <tr
                      key={r.level}
                      aria-current={isCurrent ? "true" : undefined}
                      title={isCurrent ? t("journey.currentTier") : undefined}
                      className={`border-t border-glass ${rowCls}`}
                    >
                      <td className="py-2 pe-4">
                        <span className="inline-flex items-center gap-2">
                          {/* A marker bar, so the current rung is findable by
                              shape rather than by a faint tint alone. */}
                          <span
                            aria-hidden
                            className={
                              "inline-block w-1 h-4 rounded-full " +
                              (isCurrent ? "bg-gold-linear" : "bg-transparent")
                            }
                          />
                          L{r.level}
                        </span>
                      </td>
                      <td className="py-2 pe-4">
                        <span
                          className={
                            "inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider " +
                            (TIER_STYLES[r.tier] ?? "border-glass-mid")
                          }
                        >
                          {r.tier}
                        </span>
                      </td>
                      <td className="py-2 pe-4 text-end">{formatCurrency(r.balance)}</td>
                      <td className="py-2 text-end">
                        {formatSignedCurrency(r.level_gain)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {table.length > DEFAULT_LEVELS + 1 && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setShowAllLevels((v) => !v)}
                className="btn-ghost"
              >
                {showAllLevels
                  ? t("journey.showLess", { level: DEFAULT_LEVELS })
                  : t("journey.showToLevel", { level: table.length - 1 })}
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}
